import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Shield, Lock, Mail, Loader2, AlertCircle } from "lucide-react";
import axios from "axios";
import { logger } from "@/utils/logger";

const API = process.env.REACT_APP_BACKEND_URL;

const AdminLogin = ({ onLogin }) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const res = await axios.post(`${API}/api/staff/login`, { email, password });
      if (res.data.success) {
        sessionStorage.setItem("admin_token", res.data.token);
        sessionStorage.setItem("staff_role", res.data.role);
        onLogin(res.data.token, res.data.role);
        navigate("/admin/dashboard");
      }
    } catch (err) {
      logger.error("Admin login failed:", err);
      setError(err.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
            <Shield className="w-8 h-8 text-[#D4AF37]" />
          </div>
          <h1 className="text-2xl text-[#EDEDED] font-medium">Breedling Staff</h1>
          <p className="text-[#71717A] text-sm mt-1">Secure administration and moderation access</p>
        </div>

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="glass-card p-6">
          {error && (
            <div className="mb-4 p-3 rounded-lg bg-[#EF4444]/10 border border-[#EF4444]/30 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-[#EF4444]" />
              <span className="text-sm text-[#EF4444]">{error}</span>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm text-[#A1A1AA] mb-2">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#71717A]" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@breedling.com"
                  className="input-minimal pl-10"
                  required
                  data-testid="admin-email"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm text-[#A1A1AA] mb-2">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#71717A]" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="input-minimal pl-10"
                  required
                  data-testid="admin-password"
                />
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full mt-6 py-3 bg-[#D4AF37] text-black font-medium rounded-lg hover:bg-[#B8962E] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="admin-login-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Authenticating...
              </>
            ) : (
              <>
                <Shield className="w-4 h-4" />
                Sign In
              </>
            )}
          </button>
        </form>

        <p className="text-center text-[#52525B] text-xs mt-6">
          This is a secure administrative area. Unauthorized access is prohibited.
        </p>
      </div>
    </div>
  );
};

export default AdminLogin;
